import { io } from "socket.io-client";
import { API_URL } from "../../../config/connect";
import { getCurrentBackendLanguage } from "../../../utils/apiErrorHandler";

let socketInstance = null;
const MAX_RECONNECT_ATTEMPTS = 5;

export const initializeSocket = () => {
    // Қосылым сатысындағы (connecting) socket-ті де қайта пайдаланамыз — әр түрлі
    // компоненттер бір мезгілде initializeSocket шақырса, әрекетті үзіп-үзіп
    // жаңадан құрмаймыз ("closed before the connection is established" шуының себебі).
    if (socketInstance && !socketInstance.disconnected) {
        console.log("[Socket] Reusing existing socket (connected or connecting)");
        return socketInstance;
    }

    if (socketInstance) {
        console.log("[Socket] Cleaning up existing socket before reconnect");
        socketInstance.disconnect();
        socketInstance = null;
    }

    const token = localStorage.getItem("access_token");
    console.log("[Socket] Initializing, token exists:", !!token, "URL:", API_URL);

    const socket = io(API_URL, {
        auth: {
            Authorization: `Bearer ${token}`,
            language: getCurrentBackendLanguage(),
        },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 3000,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        timeout: 10000,
    });

    socketInstance = socket;

    socket.on("connect", () => {
        console.log("[Socket] Connected! ID:", socket.id);
        localStorage.setItem("socketConnected", "true");
        window.dispatchEvent(new CustomEvent("socket:connected"));
    });

    socket.on("disconnect", (reason) => {
        console.log("[Socket] Disconnected:", reason);
        localStorage.setItem("socketConnected", "false");
        window.dispatchEvent(new CustomEvent("socket:disconnected", { detail: { reason } }));

        if (reason === "io server disconnect") {
            console.log("[Socket] Server forced disconnect, reconnecting in 3s...");
            setTimeout(() => {
                if (socketInstance && !socketInstance.connected) {
                    socketInstance.connect();
                }
            }, 3000);
        }
    });

    socket.on("connect_error", (error) => {
        console.error("[Socket] Connection error:", error.message);
        localStorage.setItem("socketConnected", "false");
        window.dispatchEvent(new CustomEvent("socket:error", { detail: { error: error.message } }));
    });

    socket.on("reconnect", (attemptNumber) => {
        console.log("[Socket] Reconnected after", attemptNumber, "attempts");
        window.dispatchEvent(new CustomEvent("socket:reconnected"));
    });

    socket.on("reconnect_attempt", (attemptNumber) => {
        console.log("[Socket] Reconnect attempt:", attemptNumber);
        window.dispatchEvent(new CustomEvent("socket:reconnect_attempt"));
    });

    socket.on("reconnect_error", (error) => {
        console.error("[Socket] Reconnect error:", error.message);
    });

    socket.on("reconnect_failed", () => {
        console.error("[Socket] Failed to reconnect after maximum attempts");
        window.dispatchEvent(new CustomEvent("socket:reconnect_failed"));
    });

    return socket;
};

export const disconnectSocket = () => {
    if (socketInstance) {
        socketInstance.disconnect();
        socketInstance = null;
    }
};

// Ескі socket-ті бұзып, жаңасын құрады — қолмен қайта қосудың (retry) сенімді жолы.
// initializeSocket connected socket-ті қайта пайдаланатындықтан, мұнда алдымен үземіз.
export const forceReconnectSocket = () => {
    if (socketInstance) {
        socketInstance.removeAllListeners();
        socketInstance.disconnect();
        socketInstance = null;
    }
    return initializeSocket();
};

export const getSocketConnectionStatus = () => {
    return localStorage.getItem("socketConnected") === "true";
};

// Live check against the in-memory socket singleton (true only if the socket
// has actually completed its handshake). Used to avoid an initial "connecting"
// flash when the socket was already opened at app boot.
export const isSocketConnectedNow = () => {
    return !!socketInstance?.connected;
};

export const subscribeToSocketEvents = (callbacks) => {
    const { onConnected, onDisconnected, onError, onReconnected, onReconnectFailed } = callbacks;

    if (onConnected) window.addEventListener("socket:connected", onConnected);
    if (onDisconnected) window.addEventListener("socket:disconnected", onDisconnected);
    if (onError) window.addEventListener("socket:error", onError);
    if (onReconnected) window.addEventListener("socket:reconnected", onReconnected);
    if (onReconnectFailed) window.addEventListener("socket:reconnect_failed", onReconnectFailed);

    return () => {
        if (onConnected) window.removeEventListener("socket:connected", onConnected);
        if (onDisconnected) window.removeEventListener("socket:disconnected", onDisconnected);
        if (onError) window.removeEventListener("socket:error", onError);
        if (onReconnected) window.removeEventListener("socket:reconnected", onReconnected);
        if (onReconnectFailed) window.removeEventListener("socket:reconnect_failed", onReconnectFailed);
    };
};

export const createChatEventEmitter = (socket, eventMap) => {
    if (!socket) return () => {};

    const entries = Object.entries(eventMap);
    for (const [eventName, handler] of entries) {
        socket.on(eventName, handler);
    }

    return () => {
        for (const [eventName, handler] of entries) {
            socket.off(eventName, handler);
        }
    };
};
